'use client';

import { Textarea } from '../../atoms/Textarea';
import { FeedbackButton } from '../../molecules/FeedbackButton/FeedbackButton';
import { FormModal } from '../../molecules/FormModal/FormModal';
import {
  MAX_FEEDBACK_CHARS,
  feedbackFormSchema,
  type FeedbackModalData,
  type FeedbackModalProps,
} from './FeedbackModal.types';

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';

export const FeedbackModal = ({
  microcopy,
  onSubmit,
  className = '',
}: FeedbackModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    watch,
  } = useForm<FeedbackModalData>({
    resolver: zodResolver(feedbackFormSchema),
    mode: 'onChange',
    defaultValues: { feedback: '' },
  });

  const watchedFeedback = watch('feedback');

  const onFormSubmit = (data: FeedbackModalData) => {
    onSubmit(data);
  };

  const triggerElement = (
    <FeedbackButton
      microcopy={{
        cta: microcopy.title,
        buttonAriaLabel: microcopy.feedbackButton.buttonAriaLabel,
        closeAriaLabel: microcopy.feedbackButton.closeAriaLabel,
      }}
      vertical
    />
  );

  const modalContent = (
    <div className="flex h-full flex-col pb-4">
      <div className="flex flex-1 flex-col">
        <Controller
          control={control}
          name="feedback"
          render={({ field }) => (
            <Textarea
              data-testid="feedback-input"
              placeholder={microcopy.placeholder}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              maxLength={MAX_FEEDBACK_CHARS}
              className="h-full min-h-24 w-full flex-1 resize-none overflow-auto rounded-lg border border-grey-300 px-3 py-2"
              aria-invalid={Boolean(errors.feedback)}
            />
          )}
        />
      </div>
      <div>
        <p className="text-xs text-grey-450">
          {`${Math.max(0, MAX_FEEDBACK_CHARS - watchedFeedback.length)}/${MAX_FEEDBACK_CHARS}`}
        </p>
      </div>
    </div>
  );

  return (
    <FormModal
      size="small"
      trigger={triggerElement}
      content={modalContent}
      title={microcopy.title}
      subtitle={microcopy.subtitle}
      description={microcopy.description}
      descriptionClassName="text-md"
      close={microcopy.close}
      submit={microcopy.primaryCta}
      disabled={!isValid}
      cancel={microcopy.secondaryCta}
      support={microcopy.support}
      onSubmit={handleSubmit(onFormSubmit)}
      className={className}
      footerClassName="static"
    />
  );
};
